import type { GridRenderEditCellParams } from '@mui/x-data-grid-pro';
import { toast } from '@tempo-io/sui-sonner';
import { isAfter } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { DatePicker } from '@/common/components/date-picker';
import { useTableCellMutation } from '../hooks/useTableCellMutation';
import type { ItemsTableRow } from '../types';
import { EditCell } from './EditCell';

type PlannedDateField = 'plannedStartDateTime' | 'plannedFinishDateTime';

export const PlannedDateEditCell = ({
  dateField,
  id,
  row,
  field,
  colDef,
  api,
}: GridRenderEditCellParams<ItemsTableRow> & { dateField: PlannedDateField }) => {
  const cellId = `${dateField}-${id}-${field}`;
  const itemId = Number(id);
  const { t } = useTranslation();

  const { commit } = useTableCellMutation();

  const currentValue = row[dateField];

  const areDatesValid = (next: string | null): boolean => {
    if (!next) return true;
    const start = dateField === 'plannedStartDateTime' ? next : row.plannedStartDateTime;
    const finish = dateField === 'plannedFinishDateTime' ? next : row.plannedFinishDateTime;
    if (!start || !finish) return true;
    return !isAfter(start, finish);
  };

  return (
    <EditCell
      width={colDef.computedWidth}
      containerProps={{ 'data-testid': cellId }}
      contentClassName="w-auto p-0"
      matchCellHeight={false}
      closeOnScrollOutOfView={{ api, id, field }}
    >
      <DatePicker
        value={currentValue}
        showClearButton
        onSelect={(next) => {
          if (!areDatesValid(next)) {
            toast.error(t(($) => $.itemsTable.plannedDateInvalidOrder));
            api.stopCellEditMode?.({ id, field, ignoreModifications: true });
            return;
          }
          commit({
            itemId,
            api,
            row,
            data: { [dateField]: next },
          });
          api.stopCellEditMode?.({ id, field, ignoreModifications: true });
        }}
      />
    </EditCell>
  );
};
